/**
 * Bug #161 — Manual in-place node-type swap control.
 *
 * Renders in the right-panel Task tab between the AI Explainer and the
 * NodePropertiesPanel. Lets an operator change a selected node's type
 * deterministically — without going through the AI — via the backend
 * CanvasNodeSwapService (POST /api/v1/canvas/swap/node).
 *
 * What it does:
 *   • Fetches the closed ontology (canvasSwapAllowedTypes) on mount.
 *   • Shows the selected node's current type + a dropdown of allowed
 *     types to swap to.
 *   • On confirm: posts current canvas + nodeId + newType, receives
 *     updated canvas + impact report, applies via canvas.loadCanvasData,
 *     surfaces impacts inline so the operator can review tail references
 *     that may no longer resolve under the new type's output schema.
 *   • Hidden when no node is selected, when the canvas is in immutable /
 *     CEG read-only mode, or when the selected node is a structural
 *     primitive that doesn't make sense to swap (start, end, etc.).
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import { ArrowRight, AlertTriangle, RefreshCw, Search, X, ChevronDown } from 'lucide-react';
import { canvasSwapNode, canvasSwapAllowedTypes } from '../../services/api';
// RC-97j — TYPE_GROUPS moved to soarNodes.js to share one taxonomy
// source with the left palette (NODE_CATEGORIES). Entries with
// isAlias: true are filtered from the picker in RC-97l render path.
import { TYPE_GROUPS as SOAR_TYPE_GROUPS } from '../../data/soarNodes';

/* Node types that should never be swap targets — start/end sentinels +
 * convergence pills that are synthesised by the canvas, not authored. */
const NON_SWAP_TARGETS = new Set(['start', 'end', 'convergence']);

/* Helper — pretty-print a snake_case canvas type for display. */
function prettyType(t) {
  if (!t) return '(unknown)';
  return t.replace(/_/g, ' ').replace(/\btask\b/i, '').trim();
}

/* RC-97j — TYPE_GROUPS taxonomy now lives in soarNodes.js (single
 * source of truth alongside NODE_CATEGORIES). The previous in-file
 * definition (Bug #170 / #173) and its operator-input notes are
 * preserved verbatim there. This file just consumes the shared
 * source, applies the alias filter for RC-97l, and renders.
 *
 * RC-97l — Aliases (entries flagged isAlias: true) are filtered from
 * the picker UI so operators see only canonical types. Aliases stay
 * in the source list (and BE ontology) for back-compat with canvases
 * authored before the rename. */
const TYPE_GROUPS = SOAR_TYPE_GROUPS.map((g) => ({
  ...g,
  types: (g.types || []).filter((t) => !t.isAlias),
})).filter((g) => g.types.length > 0);

export default function NodeTypeSwapControl({
  selectedNode,
  canvas,
  isReadOnly,
  addToast,
}) {
  const [allowedTypes, setAllowedTypes] = useState([]);
  const [loadingTypes, setLoadingTypes] = useState(false);
  const [targetType, setTargetType] = useState('');
  const [pending, setPending] = useState(false);
  const [impacts, setImpacts] = useState([]);
  const [errorMsg, setErrorMsg] = useState(null);

  /* Load the closed-ontology list once. The set is stable per backend
   * release — we cache it on the window so repeated mounts (e.g. user
   * clicking between nodes) don't re-fetch. */
  useEffect(() => {
    if (window.__cachedSwapAllowedTypes) {
      setAllowedTypes(window.__cachedSwapAllowedTypes);
      return;
    }
    let alive = true;
    setLoadingTypes(true);
    canvasSwapAllowedTypes()
      .then((res) => {
        if (!alive) return;
        const data = res?.data || res;
        const types = Array.isArray(data?.types) ? data.types : [];
        window.__cachedSwapAllowedTypes = types;
        setAllowedTypes(types);
      })
      .catch((e) => {
        if (!alive) return;
        console.warn('[NodeTypeSwap] allowed-types fetch failed', e);
      })
      .finally(() => alive && setLoadingTypes(false));
    return () => { alive = false; };
  }, []);

  /* Reset state when selection changes — stale dropdown values should not
   * leak between nodes. */
  useEffect(() => {
    setTargetType('');
    setImpacts([]);
    setErrorMsg(null);
  }, [selectedNode?.id]);

  const currentType = selectedNode?.type;
  const isSwappable = useMemo(() => {
    if (!selectedNode || isReadOnly) return false;
    if (NON_SWAP_TARGETS.has(currentType)) return false;
    return true;
  }, [selectedNode, currentType, isReadOnly]);

  if (!isSwappable) return null;

  /* Bug #170 — Render the dropdown as <optgroup>s keyed off TYPE_GROUPS
   * for navigability. The allowedTypes set (from the BE) is the source
   * of truth for what's permitted; the TYPE_GROUPS structure layers
   * grouping + hint text on top. Types in allowedTypes that aren't
   * mentioned in TYPE_GROUPS fall into an "Other" bucket so the UI
   * stays comprehensive even when new types ship before this file is
   * updated. */
  const allowedSet = new Set(allowedTypes);
  const seen = new Set();
  const groupedOptions = TYPE_GROUPS.map((g) => ({
    label: g.label,
    hint: g.hint,
    types: g.types
      .filter((t) => allowedSet.has(t.id))
      .filter((t) => t.id !== currentType && !NON_SWAP_TARGETS.has(t.id))
      .map((t) => { seen.add(t.id); return t; }),
  })).filter((g) => g.types.length > 0);

  const otherTypes = allowedTypes
    .filter((t) => !seen.has(t) && t !== currentType && !NON_SWAP_TARGETS.has(t))
    .sort()
    .map((t) => ({ id: t, hint: '' }));
  if (otherTypes.length > 0) {
    groupedOptions.push({ label: 'Other', hint: '', types: otherTypes });
  }
  const totalOptions = groupedOptions.reduce((sum, g) => sum + g.types.length, 0);

  const handleSwap = async () => {
    if (!targetType) return;
    setPending(true);
    setErrorMsg(null);
    setImpacts([]);
    try {
      const currentCanvas = {
        nodes: canvas?.nodes || [],
        connections: canvas?.connections || [],
      };
      // RC-9 (task #189) — Preserve existing config + label by default.
      // Passing `newConfig: {}` previously caused the backend to REPLACE
      // the entire config with an empty object, which nuked load-bearing
      // keys like inputParameters that are still valid on the new type
      // (e.g. inputParameters survives webhook→manual_start). The backend's
      // swap-service contract is "newConfig === null ⇒ preserve" — we now
      // honor that. Type-specific config sanitization (stripping keys that
      // are invalid for the new type, like webhook's `path` on a
      // manual_start) is the backend's responsibility, not the FE's.
      //
      // Label: we send the existing label unchanged. The backend decides
      // whether to auto-rewrite it (when the current label matches the
      // OLD type's default and is therefore auto-generated). Explicit
      // user-written labels are preserved.
      const res = await canvasSwapNode({
        currentCanvas,
        nodeId: selectedNode.id,
        newType: targetType,
        // newConfig omitted → backend preserves existing config
        // newLabel omitted → backend may auto-rewrite if it matches the old type's default
      });
      const data = res?.data || res;
      if (data?.error) {
        setErrorMsg(data.error);
        addToast && addToast(`Swap rejected: ${data.error}`, 'warn');
        return;
      }
      if (data?.canvas?.nodes) {
        canvas.loadCanvasData(
          { nodes: data.canvas.nodes, connections: data.canvas.connections || [] },
          { autoLayoutOnLoad: false }
        );
        // RC-9: persist immediately so the workflow's stored state matches
        // what the operator sees on canvas. Without this, the canvas card
        // and Code panel reflect the swap but a subsequent reload or
        // Execute reads the prior persisted state — the classic "swap
        // appeared to work, then disappeared on reload" bug. canvas.save()
        // is idempotent; if it fails, we surface the toast and leave the
        // operator with the visible-but-unsaved state.
        try {
          if (typeof canvas.save === 'function') {
            await canvas.save();
          }
        } catch (saveErr) {
          addToast && addToast(
            `Swap applied on canvas but Save failed: ${saveErr?.message || saveErr}. `
            + `Click Save manually before Executing.`,
            'warn',
          );
        }
        const impactList = Array.isArray(data.impacts) ? data.impacts : [];
        setImpacts(impactList);
        const summary = impactList.length > 0
          ? `Swapped to ${targetType} · ${impactList.length} reference(s) need review`
          : `Swapped to ${targetType} — no reference impacts`;
        addToast && addToast(summary, impactList.length > 0 ? 'warn' : 'success');
        setTargetType('');
      } else {
        setErrorMsg('Backend returned no canvas in the response.');
      }
    } catch (e) {
      setErrorMsg(e?.message || String(e));
      addToast && addToast(`Swap failed: ${e?.message || e}`, 'error');
    } finally {
      setPending(false);
    }
  };

  return (
    <div style={{
      padding: '10px 12px',
      background: 'var(--v2-bg-elevated)',
      borderTop: '1px solid var(--v2-border-default)',
      borderBottom: '1px solid var(--v2-border-default)',
      fontSize: 11.5,
    }}>
      <div style={{
        display: 'flex', alignItems: 'center', gap: 6, marginBottom: 6,
        color: 'var(--v2-text-secondary)', fontWeight: 700, fontSize: 10.5,
        textTransform: 'uppercase', letterSpacing: 0.4,
      }}>
        <RefreshCw size={11} />
        Change node type
      </div>
      <div style={{ color: 'var(--v2-text-muted)', marginBottom: 8 }}>
        Current: <code style={{
          background: 'var(--v2-bg-surface)', border: '1px solid var(--v2-border-default)', borderRadius: 4,
          padding: '1px 6px', fontFamily: 'ui-monospace, monospace',
        }}>{currentType}</code>
      </div>
      <TypePickerPopover
        groupedOptions={groupedOptions}
        targetType={targetType}
        onPick={(t) => setTargetType(t)}
        onSwap={handleSwap}
        pending={pending}
        loadingTypes={loadingTypes}
        totalOptions={totalOptions}
      />
      {loadingTypes && (
        <div style={{ marginTop: 6, color: 'var(--v2-text-disabled)' }}>Loading allowed types…</div>
      )}
      {targetType && (() => {
        // Find the hint for the picked type to show what it does.
        let hint = '';
        for (const g of groupedOptions) {
          const found = g.types.find((t) => t.id === targetType);
          if (found) { hint = found.hint; break; }
        }
        return hint ? (
          <div style={{
            marginTop: 6, padding: '6px 8px', background: 'var(--v2-action-soft)',
            border: '1px solid var(--v2-action-soft)', borderRadius: 5, color: 'var(--v2-action)',
            fontSize: 10.5, lineHeight: 1.45,
          }}>{hint}</div>
        ) : null;
      })()}
      {errorMsg && (
        <div style={{
          marginTop: 8, padding: '6px 8px', background: 'var(--v2-danger-soft)',
          border: '1px solid var(--v2-danger-soft)', borderRadius: 5, color: 'var(--v2-danger)',
        }}>{errorMsg}</div>
      )}
      {impacts.length > 0 && (
        <div style={{
          marginTop: 8, padding: '8px 10px', background: 'var(--v2-warning-soft)',
          border: '1px solid var(--v2-warning-soft)', borderRadius: 5, color: 'var(--v2-warning)',
        }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 6,
            fontWeight: 700, marginBottom: 4,
          }}>
            <AlertTriangle size={11} />
            {impacts.length} reference{impacts.length > 1 ? 's' : ''} to verify
          </div>
          <div style={{ fontSize: 10.5, lineHeight: 1.45 }}>
            The new type's output schema may differ. These references in
            other nodes' configs point at the swapped node's outputs —
            confirm each one still resolves under the new type.
          </div>
          <ul style={{
            margin: '6px 0 0 0', paddingLeft: 14, fontSize: 10.5,
            fontFamily: 'ui-monospace, monospace',
          }}>
            {impacts.slice(0, 5).map((i, idx) => (
              <li key={idx}>
                <code>{i.referencingNodeId}</code> · {i.configPath} ·{' '}
                <code>{i.expression}</code>
              </li>
            ))}
            {impacts.length > 5 && (
              <li style={{ fontFamily: 'inherit', color: 'var(--v2-warning)' }}>
                …and {impacts.length - 5} more
              </li>
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────────────
 * TypePickerPopover — Bug #172
 *
 * Replaces the native <select>+<optgroup> dropdown (which renders as a
 * full-page-height browser list) with a compact tabbed popover:
 *
 *   ┌──────────────────────────────────────────────┐
 *   │ [trigger button: current pick   ▾] [Swap →] │
 *   ├──────────────────────────────────────────────┤  ← popover, anchored
 *   │ 🔍 search                                   │     under the trigger
 *   │ [Triggers] [Human Input] [Tasks] [Data] ... │     (horizontal scroll)
 *   │ ───────────────────────────────────────────  │
 *   │ • manual_start                              │
 *   │   Manual / user-initiated; accepts input    │
 *   │ • webhook                                    │
 *   │   HTTP webhook receiver; accepts JSON       │
 *   │ ...                                          │
 *   └──────────────────────────────────────────────┘
 *
 * Closes on outside click or Escape. Search filters across all groups
 * and pulls matches into a "Search results" virtual group.
 * ──────────────────────────────────────────────────────────────────── */
function TypePickerPopover({
  groupedOptions, targetType, onPick, onSwap, pending, loadingTypes, totalOptions,
}) {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [search, setSearch] = useState('');
  const popRef = useRef(null);

  // Close on outside click + Escape
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (popRef.current && !popRef.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  // Reset active tab when popover opens or groups change
  useEffect(() => { if (open) setActiveTab(0); }, [open]);

  const searchLower = search.trim().toLowerCase();
  const filteredGroups = useMemo(() => {
    if (!searchLower) return groupedOptions;
    // Build a single "Search results" group for clarity
    const matches = [];
    for (const g of groupedOptions) {
      for (const t of g.types) {
        if (
          t.id.toLowerCase().includes(searchLower)
          || (t.hint || '').toLowerCase().includes(searchLower)
          || g.label.toLowerCase().includes(searchLower)
        ) {
          matches.push({ ...t, _group: g.label });
        }
      }
    }
    return matches.length > 0
      ? [{ label: `Search results · "${search.trim()}"`, types: matches }]
      : [];
  }, [groupedOptions, searchLower, search]);

  const activeGroup = filteredGroups[activeTab] || filteredGroups[0];
  const canSwap = !!targetType && !pending;

  return (
    <div style={{ display: 'flex', gap: 6, alignItems: 'stretch', position: 'relative' }} ref={popRef}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        disabled={pending || loadingTypes || totalOptions === 0}
        style={{
          flex: 1, fontSize: 11.5, padding: '6px 10px',
          borderRadius: 5, border: '1px solid var(--v2-border-default)',
          background: open ? 'var(--v2-action-soft)' : 'var(--v2-bg-surface)', cursor: 'pointer',
          display: 'inline-flex', alignItems: 'center', justifyContent: 'space-between',
          fontFamily: targetType ? 'ui-monospace, monospace' : 'inherit',
          color: targetType ? 'var(--v2-text-primary)' : 'var(--v2-text-muted)',
        }}
      >
        <span>{targetType || (loadingTypes ? 'Loading…' : '— pick new type —')}</span>
        <ChevronDown size={12} style={{ flexShrink: 0, marginLeft: 6, opacity: 0.6 }} />
      </button>
      <button
        type="button"
        onClick={onSwap}
        disabled={!canSwap}
        style={{
          padding: '6px 10px', fontSize: 11.5, fontWeight: 600,
          color: 'var(--v2-bg-surface)', background: canSwap ? 'var(--v2-action)' : 'var(--v2-text-disabled)',
          border: 'none', borderRadius: 5,
          cursor: canSwap ? 'pointer' : 'not-allowed',
          display: 'inline-flex', alignItems: 'center', gap: 4,
        }}
        title="Swap this node's type — preserves id, edges, position; cascades references"
      >
        {pending ? 'Swapping…' : 'Swap'} <ArrowRight size={11} />
      </button>

      {open && (
        <div
          role="dialog"
          aria-label="Pick node type"
          style={{
            position: 'absolute', top: 'calc(100% + 4px)', left: 0, right: 0,
            zIndex: 2000, background: 'var(--v2-bg-surface)',
            border: '1px solid var(--v2-border-default)', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,0.12), 0 2px 6px rgba(0,0,0,0.04)',
            display: 'flex', flexDirection: 'column',
            maxHeight: 380,
            overflow: 'hidden',
          }}
        >
          {/* Search */}
          <div style={{
            padding: '8px 10px', borderBottom: '1px solid var(--v2-bg-muted)',
            display: 'flex', alignItems: 'center', gap: 6,
          }}>
            <Search size={12} style={{ color: 'var(--v2-text-disabled)', flexShrink: 0 }} />
            <input
              autoFocus
              value={search}
              onChange={(e) => { setSearch(e.target.value); setActiveTab(0); }}
              placeholder="Search type or hint…"
              style={{
                flex: 1, border: 'none', outline: 'none', fontSize: 11.5,
                background: 'transparent', fontFamily: 'inherit',
              }}
            />
            {search && (
              <button
                type="button"
                onClick={() => setSearch('')}
                style={{
                  background: 'transparent', border: 'none', cursor: 'pointer',
                  padding: 2, color: 'var(--v2-text-disabled)', display: 'flex',
                }}
                title="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>

          {/* Tabs — horizontal scroll if many groups */}
          {filteredGroups.length > 1 && (
            <div style={{
              display: 'flex', gap: 4, padding: '6px 8px',
              borderBottom: '1px solid var(--v2-bg-muted)',
              overflowX: 'auto', whiteSpace: 'nowrap',
              flexShrink: 0,
            }}>
              {filteredGroups.map((g, i) => (
                <button
                  key={g.label}
                  type="button"
                  onClick={() => setActiveTab(i)}
                  style={{
                    fontSize: 10.5, fontWeight: 600,
                    padding: '4px 8px', borderRadius: 4,
                    border: '1px solid ' + (i === activeTab ? 'var(--v2-action)' : 'var(--v2-border-default)'),
                    background: i === activeTab ? 'var(--v2-action)' : 'var(--v2-bg-surface)',
                    color: i === activeTab ? 'var(--v2-bg-surface)' : 'var(--v2-text-secondary)',
                    cursor: 'pointer', flexShrink: 0,
                  }}
                  title={g.hint || g.label}
                >
                  {g.label}
                  <span style={{
                    marginLeft: 4, padding: '0 5px', borderRadius: 8,
                    fontSize: 9.5, fontWeight: 700,
                    background: i === activeTab ? 'rgba(255,255,255,0.25)' : 'var(--v2-bg-muted)',
                    color: i === activeTab ? 'var(--v2-bg-surface)' : 'var(--v2-text-muted)',
                  }}>{g.types.length}</span>
                </button>
              ))}
            </div>
          )}

          {/* Type list */}
          <div style={{
            flex: 1, overflowY: 'auto', padding: '4px 0',
            background: 'var(--v2-bg-elevated)',
          }}>
            {filteredGroups.length === 0 ? (
              <div style={{ padding: '14px 12px', color: 'var(--v2-text-disabled)', fontSize: 11 }}>
                No types match "{search.trim()}".
              </div>
            ) : activeGroup?.types.length > 0 ? (
              activeGroup.types.map((t) => {
                const selected = t.id === targetType;
                return (
                  <div
                    key={t.id}
                    onClick={() => { onPick(t.id); setOpen(false); }}
                    style={{
                      padding: '7px 12px', cursor: 'pointer',
                      background: selected ? 'var(--v2-success-soft)' : 'transparent',
                      borderLeft: '2px solid ' + (selected ? 'var(--v2-action)' : 'transparent'),
                    }}
                    onMouseEnter={(e) => { if (!selected) e.currentTarget.style.background = 'var(--v2-bg-muted)'; }}
                    onMouseLeave={(e) => { if (!selected) e.currentTarget.style.background = 'transparent'; }}
                  >
                    <div style={{
                      fontFamily: 'ui-monospace, monospace',
                      fontSize: 11.5, fontWeight: selected ? 700 : 500,
                      color: 'var(--v2-text-primary)',
                    }}>
                      {t.id}
                      {t._group && (
                        <span style={{
                          marginLeft: 6, fontSize: 9.5, fontWeight: 400,
                          color: 'var(--v2-text-disabled)', fontFamily: 'inherit',
                        }}>· {t._group}</span>
                      )}
                    </div>
                    {t.hint && (
                      <div style={{
                        fontSize: 10.5, color: 'var(--v2-text-muted)', marginTop: 2,
                        lineHeight: 1.4,
                      }}>{t.hint}</div>
                    )}
                  </div>
                );
              })
            ) : (
              <div style={{ padding: '14px 12px', color: 'var(--v2-text-disabled)', fontSize: 11 }}>
                This group has no types available for swapping.
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
